import { useEffect, useState } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

export default function EmailSettingsPage() {
  const [tab, setTab] = useState('smtp');
  const [form, setForm] = useState({ enabled: false, host: 'smtp.office365.com', port: 587, secure: false, user: '', from: '', order_cc: '', order_bcc: '', reply_to: '', password: '' });
  const [hasPassword, setHasPassword] = useState(false);
  const [ready, setReady] = useState(false);
  const [paused, setPaused] = useState(false);
  const [configured, setConfigured] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testTo, setTestTo] = useState('');
  const [testing, setTesting] = useState(false);

  // templates
  const [templates, setTemplates] = useState([]);
  const [activeKey, setActiveKey] = useState('');
  const [tpl, setTpl] = useState({ subject: '', body: '' });
  const [tplSaving, setTplSaving] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const loadTemplates = () => api.get('/api/settings/templates').then(r => {
    setTemplates(r.data.templates);
    if (r.data.templates.length && !activeKey) {
      setActiveKey(r.data.templates[0].key);
      setTpl({ subject: r.data.templates[0].subject, body: r.data.templates[0].body });
    }
  }).catch(() => {});

  const selectTemplate = (key) => {
    const t = templates.find(x => x.key === key);
    if (t) { setActiveKey(key); setTpl({ subject: t.subject, body: t.body }); }
  };

  const saveTemplate = async () => {
    setTplSaving(true);
    try {
      await api.put(`/api/settings/templates/${activeKey}`, tpl);
      toast.success('Template saved');
      await loadTemplates();
    } catch (e) { toast.error(e.response?.data?.error || 'Save failed'); }
    finally { setTplSaving(false); }
  };

  const resetTemplate = async () => {
    if (!window.confirm('Reset this template to the built-in default?')) return;
    try {
      await api.post(`/api/settings/templates/${activeKey}/reset`);
      const r = await api.get('/api/settings/templates');
      setTemplates(r.data.templates);
      const t = r.data.templates.find(x => x.key === activeKey);
      if (t) setTpl({ subject: t.subject, body: t.body });
      toast.success('Reverted to default');
    } catch (e) { toast.error('Reset failed'); }
  };

  const activeTpl = templates.find(x => x.key === activeKey);

  const load = () => api.get('/api/settings/email').then(r => {
    const d = r.data;
    setForm({ enabled: d.enabled, host: d.host, port: d.port, secure: d.secure, user: d.user, from: d.from, order_cc: d.order_cc || '', order_bcc: d.order_bcc || '', reply_to: d.reply_to || '', password: '' });
    setHasPassword(d.has_password);
    setReady(d.ready);
    setPaused(d.paused); setConfigured(d.configured);
    setLoading(false);
  }).catch(() => setLoading(false));

  useEffect(() => { load(); loadTemplates(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.password) delete payload.password; // don't overwrite unless a new one was typed
      await api.put('/api/settings/email', payload);
      toast.success('Email settings saved');
      await load();
    } catch (e) { toast.error(e.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const r = await api.post('/api/settings/email/test', { to: testTo });
      toast.success(r.data.message || 'Test email sent');
    } catch (e) { toast.error(e.response?.data?.error || 'Test failed'); }
    finally { setTesting(false); }
  };

  if (loading) return (<><div className="topbar"><h2>Email Settings</h2></div><div className="page"><div className="loading">Loading…</div></div></>);

  return (
    <>
      <div className="topbar"><h2>Email Settings</h2></div>
      <div className="page">
        <div className="tabs" style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
          <button className={`btn btn-sm ${tab === 'smtp' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setTab('smtp')}>SMTP / Server</button>
          <button className={`btn btn-sm ${tab === 'templates' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setTab('templates')}>Templates</button>
        </div>

        {tab === 'smtp' && (
        <div style={{ maxWidth: 640, display: 'flex', flexDirection: 'column', gap: 20 }}>

          <div className="card" style={{ borderLeft: `4px solid ${paused ? 'var(--brand-red)' : 'var(--brand-green)'}` }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 15, fontWeight: 600 }}>
              <input type="checkbox" checked={paused}
                onChange={async e => {
                  const v = e.target.checked; setPaused(v);
                  try { await api.put('/api/settings/email', { paused: v }); toast.success(v ? 'Outgoing email paused' : 'Outgoing email resumed'); }
                  catch { setPaused(!v); toast.error('Could not update'); }
                }} />
              Pause all outgoing email
            </label>
            <p className="form-hint" style={{ margin: '6px 0 0 28px' }}>
              {paused
                ? 'Paused — NO email is being sent (welcome, order confirmations, status updates, and password-reset links are all held). Your SMTP settings are kept.'
                : 'Master kill-switch. When on, every outgoing email is stopped, even if SMTP is configured. SMTP settings are preserved.'}
            </p>
          </div>

          <div className="card">
            <div className="card-header"><h3>Outgoing email (SMTP)</h3></div>
            <p className="form-hint" style={{ marginBottom: 12 }}>
              Status: {paused ? <strong style={{ color: 'var(--brand-red)' }}>paused</strong>
                : ready ? <strong style={{ color: 'var(--brand-green)' }}>configured &amp; enabled</strong>
                : configured ? <strong style={{ color: 'var(--brand-red)' }}>disabled</strong>
                : <strong style={{ color: 'var(--brand-red)' }}>not configured</strong>}.
              Used for order confirmations, status updates, and password-reset links.
            </p>

            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
                <input type="checkbox" checked={form.enabled} onChange={e => set('enabled', e.target.checked)} />
                Enable outgoing email
              </label>
            </div>

            <div className="form-row">
              <div className="form-group" style={{ flex: 2 }}>
                <label className="form-label">SMTP host</label>
                <input className="form-input" value={form.host} onChange={e => set('host', e.target.value)} placeholder="smtp.office365.com" />
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-label">Port</label>
                <input className="form-input" type="number" value={form.port} onChange={e => set('port', e.target.value)} placeholder="587" />
              </div>
            </div>

            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
                <input type="checkbox" checked={form.secure} onChange={e => set('secure', e.target.checked)} />
                Use implicit TLS (port 465). Leave OFF for Office 365 on port 587 (STARTTLS).
              </label>
            </div>

            <div className="form-group">
              <label className="form-label">Username (the Office 365 mailbox)</label>
              <input className="form-input" type="email" value={form.user} onChange={e => set('user', e.target.value)} placeholder="orders@yourcompany.com" />
            </div>

            <div className="form-group">
              <label className="form-label">Password / app password</label>
              <input className="form-input" type="password" value={form.password} onChange={e => set('password', e.target.value)}
                placeholder={hasPassword ? '•••••••• (saved — leave blank to keep)' : 'Enter the mailbox or app password'} />
              <p className="form-hint">Stored encrypted. If the account has MFA/security defaults, use an App Password (see setup notes below).</p>
            </div>

            <div className="form-group">
              <label className="form-label">From address</label>
              <input className="form-input" type="email" value={form.from} onChange={e => set('from', e.target.value)} placeholder="orders@yourcompany.com" />
              <p className="form-hint">Usually the same as the username. Must be a mailbox the account is allowed to send as.</p>
            </div>

            <div className="form-group">
              <label className="form-label">Reply-to address</label>
              <input className="form-input" type="email" value={form.reply_to} onChange={e => set('reply_to', e.target.value)} placeholder="orders@toledoticket.com" />
              <p className="form-hint">Where replies go when a customer replies to any email (the From is a no-reply sender otherwise). Leave blank to use the From address.</p>
            </div>

            <div className="form-group">
              <label className="form-label">Order CC</label>
              <input className="form-input" value={form.order_cc} onChange={e => set('order_cc', e.target.value)} placeholder="employee@toledoticket.com, sales@toledoticket.com" />
              <p className="form-hint">Copies every order email (customer confirmation + new-order notification) to these people — <strong>visible</strong> to recipients. Separate multiple addresses with commas; leave blank for none.</p>
            </div>

            <div className="form-group">
              <label className="form-label">Order BCC</label>
              <input className="form-input" value={form.order_bcc} onChange={e => set('order_bcc', e.target.value)} placeholder="employee@toledoticket.com" />
              <p className="form-hint">Same, but a <strong>hidden</strong> blind copy — recipients don't see these addresses. Separate multiple with commas; leave blank for none.</p>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save settings'}</button>
            </div>
          </div>

          <div className="card">
            <div className="card-header"><h3>Send a test email</h3></div>
            <div className="form-row" style={{ alignItems: 'flex-end' }}>
              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-label">Send test to</label>
                <input className="form-input" type="email" value={testTo} onChange={e => setTestTo(e.target.value)} placeholder="you@yourcompany.com" />
              </div>
              <div className="form-group">
                <button className="btn btn-secondary" onClick={sendTest} disabled={testing}>{testing ? 'Sending…' : 'Send test'}</button>
              </div>
            </div>
            <p className="form-hint">Save your settings first. The test verifies the connection and delivers a sample message.</p>
          </div>

          <div className="card">
            <div className="card-header"><h3>Office 365 setup notes</h3></div>
            <ol style={{ fontSize: 13, color: 'var(--gray-700)', lineHeight: 1.6, paddingLeft: 18 }}>
              <li>Use a dedicated licensed mailbox (e.g. <code>orders@yourcompany.com</code>).</li>
              <li>In the Microsoft 365 admin center, enable <strong>Authenticated SMTP</strong> for that mailbox (Users → the mailbox → Mail → Manage email apps → check “Authenticated SMTP”).</li>
              <li>Host <code>smtp.office365.com</code>, port <code>587</code>, implicit TLS OFF (STARTTLS).</li>
              <li>If the account has MFA or security defaults on, create an <strong>App Password</strong> and use it here instead of the normal password.</li>
              <li>Set both Username and From to that mailbox address, save, then send a test.</li>
            </ol>
            <p className="form-hint">Note: Microsoft has been retiring Basic Auth SMTP for some tenants. If the test fails with an auth error even with an app password, your tenant may require OAuth2 — tell your admin, or contact us to switch the app to OAuth2 sending.</p>
          </div>

        </div>
        )}

        {tab === 'templates' && (
        <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
          <div className="card" style={{ width: 240, flexShrink: 0, padding: 8 }}>
            {templates.map(t => (
              <button key={t.key}
                onClick={() => selectTemplate(t.key)}
                className="nav-link"
                style={{ width: '100%', textAlign: 'left', background: t.key === activeKey ? 'var(--gray-100)' : 'transparent' }}>
                {t.name}{t.customized ? ' •' : ''}
              </button>
            ))}
          </div>

          <div className="card" style={{ flex: 1, maxWidth: 720 }}>
            {activeTpl ? (
              <>
                <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h3>{activeTpl.name}</h3>
                  <button className="btn btn-ghost btn-sm" onClick={resetTemplate}>Reset to default</button>
                </div>
                <div className="form-group">
                  <label className="form-label">Subject</label>
                  <input className="form-input" value={tpl.subject} onChange={e => setTpl(s => ({ ...s, subject: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Body (HTML)</label>
                  <textarea className="form-textarea" style={{ minHeight: 240, fontFamily: 'monospace', fontSize: 13 }}
                    value={tpl.body} onChange={e => setTpl(s => ({ ...s, body: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Available variables</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {activeTpl.variables.map(v => (
                      <code key={v} style={{ background: 'var(--gray-100)', padding: '2px 6px', borderRadius: 4, fontSize: 12 }}>{`{{${v}}}`}</code>
                    ))}
                  </div>
                  <p className="form-hint">Insert these where you want the value to appear. Order lists use <code>{'{{item_rows}}'}</code> inside a table body.</p>
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button className="btn btn-primary" onClick={saveTemplate} disabled={tplSaving}>{tplSaving ? 'Saving…' : 'Save template'}</button>
                </div>
              </>
            ) : <div className="loading">Loading…</div>}
          </div>
        </div>
        )}

      </div>
    </>
  );
}
